import { type FC, type ReactNode, useState } from "react";

import { SelectedAddressViewCard, useFeatureFlags } from "@jti/commerce";
import { type Address } from "@jti/fe-common";
import { AddressInfoBlock, useFormContext, useTranslations } from "@jti/ui";

import type { BillingAddressFieldConfig } from "@checkout-v2/formFields/factories/billingAddressFieldFactory";
import { useSetBillingAddressOnCart } from "@checkout-v2/shared/hooks/useSetBillingAddressOnCart";
import { hasExternalAddressValidationError } from "@checkout-v2/shared/utils/externalAddressValidationErrorUtils";
import { mergeBillingAddress } from "@checkout-v2/shared/utils/mergeBillingAddress";

import { AddressFormFields } from "../AddressFormFields";

import styles from "./BillingAddressField.module.scss";

type BillingAddressFieldProps = {
  fieldConfig: BillingAddressFieldConfig;
  initialMode?: "custom" | "view";
  mode?: "custom" | "view";
  onModeChange?: (mode: "custom" | "view") => void;
  address?: Address | null;
  formTitle?: string;
  beforeSlot?: ReactNode;
};

export const BillingAddressField: FC<BillingAddressFieldProps> = ({
  fieldConfig,
  initialMode = "custom",
  mode,
  onModeChange,
  address,
  formTitle,
  beforeSlot,
}) => {
  const { __ } = useTranslations();
  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();
  const form = useFormContext();
  const { setBillingAddressOnCartResult } = useSetBillingAddressOnCart();
  const [innerMode, setInnerMode] = useState<"custom" | "view">(initialMode);

  const currentMode = mode ?? innerMode;
  const billingAddressFromForm = form.watch(fieldConfig.name) as Address | null;

  const currentAddress = mergeBillingAddress(address, billingAddressFromForm);
  const shouldRenderAddressForm = currentMode === "custom" || !currentAddress;

  const hasExternalAddressValidationBillingError = hasExternalAddressValidationError(
    setBillingAddressOnCartResult.error
  );

  const clearExternalValidationBillingError = () => {
    if (hasExternalAddressValidationBillingError) {
      setBillingAddressOnCartResult.reset();
    }
  };

  const onEditBillingAddress = () => {
    clearExternalValidationBillingError();

    if (mode === undefined) {
      setInnerMode("custom");
    }

    onModeChange?.("custom");
  };

  return (
    <div onChangeCapture={clearExternalValidationBillingError}>
      {shouldRenderAddressForm && (
        <>
          {formTitle && <h4 className={styles.formHeader}>{formTitle}</h4>}
          <AddressFormFields fieldNameBase={fieldConfig.name} beforeSlot={beforeSlot} />
        </>
      )}

      {currentMode === "view" &&
        !!currentAddress &&
        (ffCheckoutV2EnableIteration2 ? (
          <SelectedAddressViewCard
            title={__("cart.billing.title")}
            changeBtnText={__("cart.button.edit_billing")}
            onChange={onEditBillingAddress}
          >
            <AddressInfoBlock address={currentAddress} isEditable={false} hideTelephone />
          </SelectedAddressViewCard>
        ) : (
          <AddressInfoBlock
            address={currentAddress}
            editLabel={__("cart.button.edit_billing")}
            onEdit={onEditBillingAddress}
            isEditable
            hideTelephone
          />
        ))}
    </div>
  );
};
